import { useState, type ReactNode } from 'react'
import * as api from 'api/entityApi'
import { useToast } from 'components/common/Toast'
import { usePermissions } from 'hooks/usePermissions'

export interface InlineEditFieldProps {
  label: string
  required?: boolean
  /** Column name posted to `SaveInlineData` (the legacy `data-fieldname`). */
  fieldName: string
  /** Table name posted to `SaveInlineData` (the legacy `data-tablename`). */
  tableName: string
  entityId: string
  value: string
  onChange: (value: string) => void
  error?: string
  className?: string
  children: (props: { disabled: boolean }) => ReactNode
}

/**
 * The `.inlineEditbox` pattern from CreateLeadEdit: a field is read-only until the
 * pencil is clicked, then the tick saves just that column via `SaveInlineData`
 * and the cross reverts to the value the field had when editing started.
 */
export function InlineEditField({
  label,
  required,
  fieldName,
  tableName,
  entityId,
  value,
  onChange,
  error,
  className,
  children,
}: InlineEditFieldProps) {
  const toast = useToast()
  // Legacy equivalent: objPermId.Edit = ".btnEditInline, #btnSave"
  const permissions = usePermissions()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [snapshot, setSnapshot] = useState(value)

  const startEdit = () => {
    setSnapshot(value)
    setEditing(true)
  }

  const cancelEdit = () => {
    onChange(snapshot)
    setEditing(false)
  }

  const save = async () => {
    setSaving(true)
    try {
      await api.saveInlineField({ entityId, tableName, fieldName, fieldValue: value })
      toast.push('success', `${label} saved.`)
      setEditing(false)
    } catch (err) {
      toast.push('error', `Could not save ${label}: ${(err as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={`form-group ${className ?? ''}`}>
      <label>
        {label}
        {required && <span className="red">*</span>}
      </label>

      <div className="inline-editbox">
        {children({ disabled: !editing })}

        <span className="inline-edit-btn">
          {editing ? (
            <>
              <button
                type="button"
                className={saving ? 'saving' : ''}
                disabled={saving}
                onClick={save}
                title={`Save ${label}`}
              >
                💾
              </button>
              <button type="button" disabled={saving} onClick={cancelEdit} title="Cancel">
                ✕
              </button>
            </>
          ) : (
            permissions.Edit && (
              <button type="button" onClick={startEdit} title={`Edit ${label}`}>
                ✎
              </button>
            )
          )}
        </span>
      </div>

      {error && <div className="field-error">{error}</div>}
    </div>
  )
}
